import React, { useState, useEffect, useMemo } from 'react';
import {
    Plus,
    Edit3,
    Trash2,
    Users,
    Activity,
    IdCard,
    GraduationCap,
    ShieldCheck,
    ArrowUpDown,
    ChevronUp,
    ChevronDown,
    Check,
    ChevronRight,
    ChevronLeft,
    User,
    ClipboardList,
    Lock,
    Eye,
    X,
    FileSpreadsheet,
    Users2,
    Mail,
    Search,
    Filter,
    Download,
    UserCheck,
    UserX,
    Info
} from 'lucide-react';
import FamilyLinkModal from './FamilyLinkModal';
import PersonWizardModal from './PersonWizardModal';
import './PeopleManager.css';
import API_BASE_URL from '../../config/api';

const getCleanEmail = (email) => {
    if (!email) return '';
    const atIdx = email.lastIndexOf('@');
    const localPart = atIdx > 0 ? email.substring(0, atIdx) : email;
    const domain = atIdx > 0 ? email.substring(atIdx) : '';
    const plusIdx = localPart.indexOf('+');
    return plusIdx > -1 ? localPart.substring(0, plusIdx) + domain : email;
};

const PeopleManager = () => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [sports, setSports] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingUserId, setEditingUserId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        profileType: 'all',
        roleId: null,
        isActive: true,
        teamId: '',
        sportId: '',
        search: ''
    });
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [showFamilyModal, setShowFamilyModal] = useState(false);
    const [selectedFamilyUser, setSelectedFamilyUser] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [editingMemberId, setEditingMemberId] = useState(null);
    const [tempMembershipNumber, setTempMembershipNumber] = useState('');
    useEffect(() => {
        setPage(1); // Reset to first page when filters change
    }, [filters]);

    useEffect(() => {
        fetchUsers();
    }, [filters, page, pageSize]);

    useEffect(() => {
        fetchRoles();
        fetchSports();
        fetchTeams();
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setFilters(prev => ({ ...prev, search: searchTerm }));
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);
    const fetchUsers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();

            if (filters.profileType !== 'all') params.append('profileType', filters.profileType);
            if (filters.roleId) params.append('roleId', filters.roleId);
            if (filters.isActive !== null) params.append('isActive', filters.isActive);
            if (filters.teamId) params.append('teamId', filters.teamId);
            if (filters.sportId) params.append('sportId', filters.sportId);
            if (filters.search) params.append('search', filters.search);

            // Add pagination params
            params.append('page', page);
            params.append('pageSize', pageSize);

            const response = await fetch(`${API_BASE_URL}/api/users?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });


            if (response.ok) {
                const data = await response.json();
                setUsers(data.items || []);
                setTotalCount(data.totalCount || 0);
                setTotalPages(data.totalPages || 0);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/roles`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setRoles(data);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const fetchSports = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/sports/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSports(data.filter(s => s.isActive));
            }
        } catch (error) {
            console.error('Error fetching sports:', error);
        }
    };

    const fetchTeams = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setTeams(data.filter(t => t.isActive));
            }
        } catch (error) {
            console.error('Error fetching teams:', error);
        }
    };

    const getProfileBadges = (user) => {
        const badges = [];
        const roleNames = user.roles ? user.roles.map(r => typeof r === 'string' ? r : r.name) : [];
        if (roleNames.includes('Admin')) {
            badges.push({ type: 'admin', label: 'Admin', icon: <ShieldCheck size={14} /> });
        }
        if (user.hasAthleteProfile) badges.push({ type: 'athlete', label: 'Atleta', icon: <Activity size={14} /> });

        if (user.hasMemberProfile && !user.hasAthleteProfile) badges.push({ type: 'member', label: 'Sócio', icon: <IdCard size={14} /> });

        if (user.hasCoachProfile) badges.push({ type: 'coach', label: 'Treinador', icon: <GraduationCap size={14} /> });
        if (user.hasCoordinatorProfile || roleNames.includes('Coordenador') || roleNames.includes('Coordinator')) {
            badges.push({ type: 'coordinator', label: 'Coordenador', icon: <Users size={14} /> });
        }
        return badges;
    };
    const handleEdit = (user) => {
        setEditingUserId(user.id);
        setShowModal(true);
    };

    const handleCloseWizard = () => {
        setShowModal(false);
        setEditingUserId(null);
    };

    const handleUpdateMembershipNumber = async (userId, user) => {
        if (tempMembershipNumber === user.membershipNumber) {
            setEditingMemberId(null);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            // We need to fetch current member profile details to update just the number
            // or we can just send the new number if the backend allows partial updates in the PUT.
            // Based on my backend change, PUT api/users/{id}/member-profile handles MembershipNumber.
            // However, we need to provide the other required fields like MembershipStatus and MemberSince.
            // Since we don't have full member profile in the 'user' object from the list (only membershipNumber),
            // we should fetch the user details first or rely on the fact that the list might have enough.
            // Actually, 'user' from the list has: hasMemberProfile, membershipNumber.
            // Let's fetch details to be safe and avoid zeroing out other fields.

            const detailResponse = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!detailResponse.ok) throw new Error('Erro ao obter detalhes do sócio');
            const data = await detailResponse.json();

            if (!data.memberProfile) throw new Error('Utilizador não tem perfil de sócio');

            const response = await fetch(`${API_BASE_URL}/api/users/${userId}/member-profile`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    membershipNumber: tempMembershipNumber,
                    membershipStatus: data.memberProfile.membershipStatus,
                    memberSince: data.memberProfile.memberSince,
                    paymentPreference: data.memberProfile.paymentPreference,
                    quotaPaidUntil: data.memberProfile.quotaPaidUntil
                })
            });

            if (response.ok) {
                setEditingMemberId(null);
                fetchUsers();
            } else {
                alert('Erro ao atualizar número de sócio');
            }
        } catch (error) {
            console.error('Error updating membership number:', error);
            alert('Erro ao atualizar número de sócio');
        }
    };

    const handleResendActivation = async (userId) => {
        if (!window.confirm('Tem a certeza que deseja reenviar o email de ativação para este utilizador?')) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/users/${userId}/resend-activation`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                alert('Email de ativação reenviado com sucesso!');
            } else {
                const data = await response.json().catch(() => ({}));
                alert(data.message || 'Erro ao reenviar email de ativação');
            }
        } catch (error) {
            console.error('Error resending activation email:', error);
            alert('Erro ao reenviar email de ativação');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Tem a certeza que deseja desativar este utilizador?')) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/users/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                fetchUsers();
            } else {
                alert('Erro ao desativar utilizador');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('Erro ao desativar utilizador');
        }
    };
    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const sortedUsers = useMemo(() => {
        let sortableUsers = [...users];
        if (sortConfig.key !== null) {
            sortableUsers.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];
                if (sortConfig.key === 'profiles') {
                    aValue = getProfileBadges(a).map(b => b.label).join('');
                    bValue = getProfileBadges(b).map(b => b.label).join('');
                } else if (sortConfig.key === 'team') {
                    aValue = a.currentTeam || '';
                    bValue = b.currentTeam || '';
                } else if (sortConfig.key === 'sport') {
                    aValue = a.sport || '';
                    bValue = b.sport || '';
                } else if (sortConfig.key === 'paidUntil') {
                    aValue = a.paidUntil || '';
                    bValue = b.paidUntil || '';
                } else {
                    aValue = aValue ? aValue.toString().toLowerCase() : '';
                    bValue = bValue ? bValue.toString().toLowerCase() : '';
                }
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableUsers;
    }, [users, sortConfig]);

    const getSortIcon = (name) => {
        if (sortConfig.key !== name) return <ArrowUpDown size={14} className="sort-icon" />;
        return sortConfig.direction === 'ascending' ? <ChevronUp size={14} className="sort-icon active" /> : <ChevronDown size={14} className="sort-icon active" />;
    };
    // Helper to get membership status label
    const getMemberStatusLabel = (status) => {
        const s = parseInt(status);
        if (s === 0) return 'Pendente';
        if (s === 1) return 'Ativo';
        if (s === 2) return 'Inativo';
        return '-';
    };
    const handleExport = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();

            // Use same filters as current view
            if (filters.profileType !== 'all') params.append('profileType', filters.profileType);
            if (filters.roleId) params.append('roleId', filters.roleId);
            if (filters.isActive !== null) params.append('isActive', filters.isActive);
            if (filters.teamId) params.append('teamId', filters.teamId);
            if (filters.sportId) params.append('sportId', filters.sportId);
            if (filters.search) params.append('search', filters.search);

            // Fetch ALL records (using a very large pageSize to bypass pagination)
            params.append('page', 1);
            params.append('pageSize', 10000);

            const response = await fetch(`${API_BASE_URL}/api/users?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                alert('Erro ao exportar dados.');
                return;
            }

            const data = await response.json();
            const allUsers = data.items || [];

            if (allUsers.length === 0) {
                alert('Não há dados para exportar.');
                return;
            }

            // Define headers
            const headers = [
                'Nome Completo',
                'Email',
                'Telefone',
                'NIF',
                'Data Nascimento',
                'Morada',
                'Código Postal',
                'Cidade',
                'Perfis',
                'Modalidade',
                'Equipa',
                'Estado Sócio',
                'Nº Sócio',
                'Pago Até',
                'Funções'
            ];

            // Format data rows
            const rows = allUsers.map(user => {
                const profiles = getProfileBadges(user).map(b => b.label).join(', ');
                const rolesNames = user.roles ? user.roles.map(r => r.name).join(', ') : '';
                const memberStatus = (user.hasMemberProfile && user.memberProfile) ? getMemberStatusLabel(user.memberProfile.membershipStatus) : '-';

                // Strip alias from email
                const cleanEmail = getCleanEmail(user.email);

                return [
                    `"${user.fullName || ''}"`,
                    `"${cleanEmail}"`,
                    `="${user.phone || ''}"`,
                    `="${user.nif || ''}"`,
                    `"${user.birthDate ? user.birthDate.split('T')[0] : ''}"`,
                    `"${user.address || ''}"`,
                    `"${user.postalCode || ''}"`,
                    `"${user.city || ''}"`,
                    `"${profiles}"`,
                    `"${user.sport || ''}"`,
                    `"${user.currentTeam || ''}"`,
                    `"${memberStatus}"`,
                    `="${user.membershipNumber || ''}"`,
                    `"${user.paidUntil || '-'}"`,
                    `"${rolesNames}"`
                ].join(';');
            });

            // Combine headers and rows
            const csvContent = '\uFEFF' + [headers.join(';'), ...rows].join('\n'); // Add BOM for Excel support

            // Create blob and download link
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `lista_pessoas_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting users:', error);
            alert('Ocorreu um erro ao exportar os dados.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="people-manager">
            <div className="manager-header">
                <div className="header-content">
                    <h1>Gestão de Pessoas</h1>
                    <p className="header-subtitle">Gerir atletas, sócios, treinadores e utilizadores do clube</p>
                </div>
                <div className="header-actions">
                    <button className="btn-export" onClick={handleExport}>
                        <FileSpreadsheet size={18} />
                        <span>Exportar Excel</span>
                    </button>
                    <button className="btn-add" onClick={() => setShowModal(true)}>
                        <Plus size={18} />
                        <span>Adicionar Pessoa</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-card">
                <div className="filters-container">
                    <div className="filter-group">
                        <div className="filter-header">
                            <label className="filter-label">Perfil</label>
                        </div>
                        <select
                            className="filter-select"
                            value={filters.profileType}
                            onChange={(e) => setFilters({ ...filters, profileType: e.target.value, page: 1 })}
                        >
                            <option value="all">Todos</option>
                            <option value="athlete">Atletas</option>
                            <option value="member">Sócios</option>
                            <option value="coach">Treinadores</option>
                            <option value="coordinator">Coordenadores</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <div className="filter-header">
                            <label className="filter-label">Estado</label>
                        </div>
                        <select
                            className="filter-select"
                            value={filters.isActive === null || filters.isActive === undefined ? 'all' : (filters.isActive ? 'active' : 'inactive')}
                            onChange={(e) => {
                                const val = e.target.value;
                                setFilters({
                                    ...filters,
                                    isActive: val === 'active' ? true : (val === 'inactive' ? false : null),
                                    page: 1
                                });
                            }}
                        >
                            <option value="active">Ativos</option>
                            <option value="inactive">Desistências / Inativos</option>
                            <option value="all">Todos os Estados</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <div className="filter-header">
                            <label className="filter-label">Equipa</label>
                        </div>
                        <select
                            className="filter-select"
                            value={filters.teamId || ''}
                            onChange={(e) => setFilters({ ...filters, teamId: e.target.value ? parseInt(e.target.value) : '' })}
                        >
                            <option value="">Todas</option>
                            {teams.map(team => (
                                <option key={team.id} value={team.id}>{team.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <div className="filter-header">
                            <label className="filter-label">Modalidade</label>
                        </div>
                        <select
                            className="filter-select"
                            value={filters.sportId || ''}
                            onChange={(e) => setFilters({ ...filters, sportId: e.target.value ? parseInt(e.target.value) : '' })}
                        >
                            <option value="">Todas</option>
                            {sports.map(sport => (
                                <option key={sport.id} value={sport.id}>{sport.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group search-group">
                        <div className="filter-header">
                            <label className="filter-label">Pesquisar</label>
                        </div>
                        <div className="search-wrapper">
                            <Search className="search-icon" />
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Nome, email ou NIF..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>A carregar utilizadores...</p>
                </div>
            ) : users.length === 0 ? (
                <div className="empty-state">
                    <Users size={64} className="empty-icon" />
                    <h3>Nenhum utilizador encontrado</h3>
                    <p>Adicione o primeiro utilizador do clube</p>
                    <button className="btn-add" onClick={() => setShowModal(true)}>
                        <Plus size={18} /> Adicionar Pessoa
                    </button>
                </div>
            ) : (
                <>
                    <div className="users-table-container">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('fullName')} style={{ cursor: 'pointer' }}>
                                        <div className="th-content">Nome {getSortIcon('fullName')}</div>
                                    </th>
                                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                                        <div className="th-content">Email {getSortIcon('email')}</div>
                                    </th>
                                    <th onClick={() => handleSort('phone')} style={{ cursor: 'pointer' }}>
                                        <div className="th-content">Telefone {getSortIcon('phone')}</div>
                                    </th>
                                    <th onClick={() => handleSort('profiles')} style={{ cursor: 'pointer' }}>
                                        <div className="th-content">Perfis {getSortIcon('profiles')}</div>
                                    </th>
                                    <th onClick={() => handleSort('sport')} style={{ cursor: 'pointer' }}>
                                        <div className="th-content">Modalidade {getSortIcon('sport')}</div>
                                    </th>
                                    <th onClick={() => handleSort('team')} style={{ cursor: 'pointer' }}>
                                        <div className="th-content">Equipa {getSortIcon('team')}</div>
                                    </th>
                                    <th onClick={() => handleSort('paidUntil')} style={{ cursor: 'pointer' }}>
                                        <div className="th-content">Pago Até {getSortIcon('paidUntil')}</div>
                                    </th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedUsers.map((user) => (
                                    <tr key={user.id}>
                                        <td data-label="Nome">
                                            <div className="user-name-container" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                                                <span className="user-name">{user.fullName}</span>
                                                {user.hasMemberProfile && (
                                                    <div className="socio-number-edit">
                                                        {editingMemberId === user.id ? (
                                                            <input
                                                                autoFocus
                                                                type="text"
                                                                className="table-inline-input"
                                                                value={tempMembershipNumber}
                                                                onChange={(e) => setTempMembershipNumber(e.target.value)}
                                                                onBlur={() => handleUpdateMembershipNumber(user.id, user)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleUpdateMembershipNumber(user.id, user);
                                                                    if (e.key === 'Escape') setEditingMemberId(null);
                                                                }}
                                                            />
                                                        ) : (
                                                            <span
                                                                className="socio-number clickable"
                                                                onClick={() => {
                                                                    setEditingMemberId(user.id);
                                                                    setTempMembershipNumber(user.membershipNumber || '');
                                                                }}
                                                                title="Clique para editar número de sócio"
                                                            >
                                                                {user.membershipNumber || 'S/N'}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td data-label="Email" title={getCleanEmail(user.email)}>{getCleanEmail(user.email)}</td>
                                        <td data-label="Telefone">{user.phone || '-'}</td>
                                        <td className="profiles-cell" data-label="Perfis">
                                            <div className="profile-badges-container">
                                                {getProfileBadges(user).map((badge, idx) => (
                                                    <span key={idx} className={`profile-badge ${badge.type}`}>
                                                        {badge.icon} {badge.label}
                                                    </span>
                                                ))}
                                                {getProfileBadges(user).length === 0 && '-'}
                                            </div>
                                        </td>
                                        <td data-label="Modalidade">{user.sport || '-'}</td>
                                        <td data-label="Equipa">{user.currentTeam || '-'}</td>
                                        <td data-label="Pago Até">
                                            {user.paidUntil ? (
                                                <span className="paid-until-badge" style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    padding: '4px 8px',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#ecfdf5',
                                                    color: '#047857',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '600',
                                                    border: '1px solid #a7f3d0'
                                                }}>
                                                    {user.paidUntil}
                                                </span>
                                            ) : (
                                                <span style={{ color: '#94a3b8' }}>-</span>
                                            )}
                                        </td>
                                        <td className="actions-cell" data-label="Ações">
                                            <div className="actions-buttons">
                                            {!user.lastLogin && (
                                                <button className="action-btn mail" style={{ color: '#0ea5e9' }} onClick={() => handleResendActivation(user.id)} title="Reenviar Email de Ativação">
                                                    <Mail size={16} />
                                                </button>
                                            )}
                                            <button className="action-btn family" onClick={() => { setSelectedFamilyUser(user); setShowFamilyModal(true); }} title="Associar Familiar">
                                                <Users2 size={16} />
                                            </button>
                                            <button className="action-btn edit" onClick={() => handleEdit(user)} title="Editar">
                                                <Edit3 size={16} />
                                            </button>
                                            <button className="action-btn delete" onClick={() => handleDelete(user.id)} title="Desativar">
                                                <UserX size={16} />
                                            </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="pagination-container">
                            <div className="pagination-info">
                                A mostrar {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} de {totalCount} pessoas
                            </div>
                            <div className="pagination-controls">
                                <button
                                    className="pagination-btn"
                                    onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                    disabled={page === 1}
                                >
                                    <ChevronLeft size={16} /> Anterior
                                </button>

                                <div className="pagination-pages">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        // Logic to show a window of pages around current page
                                        let pageNum;
                                        if (totalPages <= 5) pageNum = i + 1;
                                        else if (page <= 3) pageNum = i + 1;
                                        else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                                        else pageNum = page - 2 + i;

                                        return (
                                            <button
                                                key={pageNum}
                                                className={`page-number ${page === pageNum ? 'active' : ''}`}
                                                onClick={() => setPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    className="pagination-btn"
                                    onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={page === totalPages}
                                >
                                    Seguinte <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            <PersonWizardModal
                isOpen={showModal}
                userId={editingUserId}
                onClose={handleCloseWizard}
                onSaved={fetchUsers}
            />

            {/* Family Association Modal */}
            <FamilyLinkModal
                isOpen={showFamilyModal}
                onClose={() => { setShowFamilyModal(false); setSelectedFamilyUser(null); }}
                user={selectedFamilyUser}
            />
        </div>
    );
};

export default PeopleManager;